import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Header.css'; // Import CSS file for animations

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [animationPlayed, setAnimationPlayed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Set animation as played after a delay
    const timer = setTimeout(() => {
      setAnimationPlayed(true);
    }, 4000); // Updated to match the longer animation duration (3.5s) plus buffer

    return () => clearTimeout(timer);
  }, []);

  const handleSectionClick = (e, sectionId) => {
    if (isHomePage) {
      // If we're on the home page, just scroll to the section
      e.preventDefault();
      document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
    }
    // If we're not on the home page, the Link component will navigate to /#sectionId
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (isHomePage) {
      // If already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // If on another page, navigate to home
      navigate('/');
    }
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-primary bg-opacity-80 backdrop-blur-md shadow-md' : 'bg-gradient-to-b from-background to-transparent'
    }`}>
      <div className="container mx-auto px-4 md:px-6 py-6 flex justify-between items-center">
        <Link to="/" onClick={handleHomeClick} className="text-2xl font-bold text-textColor tracking-wide relative flex items-center">
          <span className={!animationPlayed ? 'logo-humaniti' : ''}>Humaniti</span>
          <span className={`ml-0.5 ${!animationPlayed ? 'logo-ai' : ''}`} style={{ position: 'relative', color: '#3d7a70' }}>.ai
            {/* Custom underline that appears after animation and stays */}
            <div className={!animationPlayed ? 'logo-underline' : ''} style={animationPlayed ? { position: 'absolute', bottom: '2px', left: 0, height: '2px', width: '100%', backgroundColor: '#3d7a70' } : {}}></div>
          </span>
        </Link>
        
        {/* Mobile menu button */}
        <button 
          className="md:hidden text-lightText hover:text-purple-500 focus:outline-none"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
        
        {/* Desktop navigation */}
        <nav className="hidden md:block">
          <ul className="flex gap-8 items-center">
            <li>
              <Link to="/" onClick={handleHomeClick} className="text-lightText hover:text-purple-500 transition-colors duration-300 font-medium">
                Home
              </Link>
            </li>
            <li>
              <Link 
                to="/#features" 
                className="text-lightText hover:text-purple-500 transition-colors duration-300 font-medium"
                onClick={(e) => handleSectionClick(e, 'features')}
              >
                Features
              </Link>
            </li>
            <li>
              <Link 
                to="/#about" 
                className="text-lightText hover:text-purple-500 transition-colors duration-300 font-medium"
                onClick={(e) => handleSectionClick(e, 'about')}
              >
                About
              </Link>
            </li>
            <li>
              <Link 
                to="/#waitlist" 
                className="bg-purple-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-all duration-300"
                onClick={(e) => handleSectionClick(e, 'waitlist')}
              >
                Request Early Access
              </Link>
            </li>
          </ul>
        </nav>
      </div>
      
      {/* Mobile navigation */}
      <div className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'} bg-primary bg-opacity-95 backdrop-blur-md shadow-md`}>
        <nav className="container mx-auto px-4 py-4">
          <ul className="flex flex-col gap-4">
            <li>
              <Link 
                to="/" 
                onClick={handleHomeClick} 
                className="block text-lightText hover:text-purple-500 transition-colors duration-300 font-medium py-2"
              >
                Home
              </Link>
            </li>
            <li>
              <Link 
                to="/#features" 
                className="block text-lightText hover:text-purple-500 transition-colors duration-300 font-medium py-2"
                onClick={(e) => handleSectionClick(e, 'features')}
              >
                Features
              </Link>
            </li>
            <li>
              <Link 
                to="/#about" 
                className="block text-lightText hover:text-purple-500 transition-colors duration-300 font-medium py-2"
                onClick={(e) => handleSectionClick(e, 'about')}
              >
                About
              </Link>
            </li>
            <li className="py-2">
              <Link 
                to="/#waitlist" 
                className="inline-block bg-purple-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 transition-all duration-300"
                onClick={(e) => handleSectionClick(e, 'waitlist')}
              >
                Request Early Access
              </Link>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
